/**
 * A monitoring frame of another schema as JSON, read with the schema it was
 * written with: every field the frame holds, including those this bundle's
 * schema does not have, and every enum value by its name.
 *
 * The writer's schema comes from the server's schema API (`fetchSchema`),
 * checked against the SHA-256 the frame's envelope names.
 */

import { reflectUnpack } from "../reflection/reflection-json";
import {
  loadSchema,
  ReflectionBaseType,
  type ReflectionObject,
  type ReflectionSchema,
} from "../reflection/reflection";
import type { SkewFrame } from "../schemaSkew";
import { fetchSchema } from "./schemaFetch";

/** Bytes before a size-prefixed FlatBuffer's root offset. */
const SIZE_PREFIX = 4;

const BATCHED_FRONT_END_MESSAGE = "BatchedFrontEnd.BatchedFrontEndMessage";

type Value = unknown;

/** The name `schema`'s enum at `index` gives `value`, or the number marked unnamed. */
function enumName(schema: ReflectionSchema, index: number, value: Value): Value {
  if (typeof value !== "number" && typeof value !== "bigint") {
    return value;
  }
  const named = schema
    .enums()
    [index]?.values()
    .find((candidate) => candidate.value() === BigInt(value));
  return named ? named.name() : `${String(value)} (unnamed)`;
}

/** `record`, a table or struct of `object` as `reflectUnpack` gives it, with its enums named. */
function nameEnums(
  schema: ReflectionSchema,
  object: ReflectionObject,
  record: Record<string, Value>,
): Record<string, Value> {
  const named: Record<string, Value> = { ...record };
  const objects = schema.objects();
  object.fields().forEach((field) => {
    const name = field.name();
    const type = field.type();
    const value = record[name];
    if (value === undefined || value === null || type === null) {
      return;
    }
    const base = type.baseType();
    const index = type.index();
    const nested = (target: number, item: Value): Value =>
      typeof item === "object" && item !== null && objects[target]
        ? nameEnums(schema, objects[target], item as Record<string, Value>)
        : item;

    if (base === ReflectionBaseType.Obj) {
      named[name] = nested(index, value);
    } else if (base === ReflectionBaseType.Vector && Array.isArray(value)) {
      if (type.element() === ReflectionBaseType.Obj) {
        named[name] = value.map((item) => nested(index, item));
      } else if (index >= 0) {
        named[name] = value.map((item) => enumName(schema, index, item));
      }
    } else if (base === ReflectionBaseType.Union) {
      // reflectUnpack names the variant in `<field>_type`.
      const variant = schema.enums()[index]?.valueByName(String(record[`${name}_type`]));
      const target = variant?.unionType()?.index() ?? -1;
      named[name] = target >= 0 ? nested(target, value) : value;
    } else if (base !== ReflectionBaseType.UType && index >= 0) {
      named[name] = enumName(schema, index, value);
    }
  });
  return named;
}

/** JSON that writes 64-bit integers as their decimal digits. */
function stringify(value: Value): string {
  return JSON.stringify(value, (_, item) => (typeof item === "bigint" ? item.toString() : item), 2);
}

/**
 * `frame` as JSON, read with its writer's schema from the schema API at
 * `baseUrl`, or why it cannot be.
 */
async function frameJson(
  baseUrl: string,
  frame: Pick<SkewFrame, "schema" | "typeName" | "payload">,
  authorization?: string,
): Promise<{ json: string } | { error: string }> {
  const fetched = await fetchSchema(baseUrl, frame.schema, authorization);
  if (fetched === null) {
    return { error: `the server could not serve schema ${frame.schema.slice(0, 8)}` };
  }
  const schema = loadSchema(fetched.bfbs);
  const rootType = fetched.rootType || frame.typeName;
  const root = schema.objectByName(rootType);
  if (!root) {
    return { error: `schema ${frame.schema.slice(0, 8)} has no ${rootType || "root type"}` };
  }
  const buf =
    rootType === BATCHED_FRONT_END_MESSAGE ? frame.payload.subarray(SIZE_PREFIX) : frame.payload;
  try {
    const record = reflectUnpack(schema, rootType, buf);
    if (record === null) {
      return { error: `the frame is not a ${rootType}` };
    }
    return { json: stringify(nameEnums(schema, root, record)) };
  } catch (error) {
    return { error: `the frame cannot be read: ${String(error)}` };
  }
}

export default frameJson;
